"use client";

import React from "react";
import {
  X,
  ShieldCheck,
  Calendar,
  Building2,
  Stethoscope,
  CreditCard,
  User,
  Heart,
  Award,
  Clock,
  Mail,
  Phone,
} from "lucide-react";
import { InsurancePolicyLead } from "@/lib/demos/insuranceDb";

interface InsuranceDetailModalProps {
  policy: InsurancePolicyLead;
  onClose: () => void;
  onAdvanceStage?: (targetStage: InsurancePolicyLead["stage"]) => void;
}

export default function InsuranceDetailModal({
  policy,
  onClose,
  onAdvanceStage,
}: InsuranceDetailModalProps) {
  const sym = policy.currency === "USD" ? "$" : "₹";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm overflow-y-auto">
      <div className="bg-slate-900 border border-blue-900/60 rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl relative my-8">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 bg-slate-950 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600/20 border border-blue-500/40 flex items-center justify-center text-blue-400">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-white">{policy.prospect_name}</h3>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-blue-950 text-blue-300 border border-blue-800">
                  {policy.stage}
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                Case No: <span className="font-mono text-slate-300">{policy.proposal_number}</span>
                {policy.policy_number && (
                  <> • Policy: <span className="font-mono text-emerald-400 font-bold">{policy.policy_number}</span></>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-6 text-xs max-h-[75vh] overflow-y-auto">
          
          {/* Top Quick Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-3 rounded-xl bg-slate-950 border border-slate-800">
              <div className="text-[10px] text-slate-500 font-semibold uppercase">Sum Assured</div>
              <div className="text-sm font-extrabold text-blue-400 mt-0.5">
                {sym}{policy.coverage_amount.toLocaleString()}
              </div>
            </div>
            <div className="p-3 rounded-xl bg-slate-950 border border-slate-800">
              <div className="text-[10px] text-slate-500 font-semibold uppercase">Annual Premium</div>
              <div className="text-sm font-extrabold text-white mt-0.5">
                {sym}{policy.annual_premium.toLocaleString()}
              </div>
            </div>
            <div className="p-3 rounded-xl bg-slate-950 border border-slate-800">
              <div className="text-[10px] text-slate-500 font-semibold uppercase">Applicant Age</div>
              <div className="text-sm font-bold text-slate-200 mt-0.5">
                {policy.age} Yrs {policy.is_tobacco_user ? "• Smoker" : "• Non-Smoker"}
              </div>
            </div>
            <div className="p-3 rounded-xl bg-slate-950 border border-slate-800">
              <div className="text-[10px] text-slate-500 font-semibold uppercase">e-KYC Status</div>
              <div className="text-sm font-bold text-emerald-400 mt-0.5 truncate">
                {policy.kyc_status || "Verified"}
              </div>
            </div>
          </div>

          {/* Section 1: Life Assured & Nominee */}
          <div className="p-4 rounded-xl bg-slate-950/80 border border-slate-800/80 space-y-3">
            <div className="text-[11px] font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
              <User className="w-3.5 h-3.5 text-blue-400" />
              Life Assured &amp; Beneficiary Profile
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <span className="text-slate-500 block">Contact Information:</span>
                <div className="font-semibold text-white mt-0.5 flex items-center gap-1.5">
                  <Mail className="w-3 h-3 text-slate-400" />
                  {policy.email}
                </div>
                <div className="text-slate-300 mt-0.5 flex items-center gap-1.5">
                  <Phone className="w-3 h-3 text-slate-400" />
                  {policy.phone || "Not specified"}
                </div>
              </div>
              <div>
                <span className="text-slate-500 block">Designated Legal Nominee:</span>
                <div className="font-semibold text-white mt-0.5 flex items-center gap-1.5">
                  <Heart className="w-3 h-3 text-rose-400" />
                  {policy.nominee_name || "Primary Legal Heir"} ({policy.nominee_relation || "Spouse"})
                </div>
                <div className="text-slate-400 mt-0.5">Nominee Share: 100% Absolute Beneficial Interest</div>
              </div>
            </div>
          </div>

          {/* Section 2: Product & Underwriting Intelligence */}
          <div className="p-4 rounded-xl bg-slate-950/80 border border-slate-800/80 space-y-3">
            <div className="text-[11px] font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
              <Stethoscope className="w-3.5 h-3.5 text-indigo-400" />
              Underwriting Assessment &amp; Medical Diagnostic Status
            </div>

            <div className="space-y-2">
              <div className="flex justify-between border-b border-slate-800/60 pb-1.5">
                <span className="text-slate-400">Coverage Plan:</span>
                <span className="text-white font-semibold">{policy.product_name}</span>
              </div>
              <div className="flex justify-between border-b border-slate-800/60 pb-1.5">
                <span className="text-slate-400">Underwriting Decision:</span>
                <span className="text-blue-300 font-medium">{policy.underwriting_decision || "Risk illustration generated"}</span>
              </div>
              <div className="flex justify-between border-b border-slate-800/60 pb-1.5">
                <span className="text-slate-400">Medical Examination Slot:</span>
                <span className="text-white">{policy.medical_exam_date || "Pending underwriter allocation"}</span>
              </div>
              <div className="flex justify-between border-b border-slate-800/60 pb-1.5">
                <span className="text-slate-400">Allocated Diagnostic Center:</span>
                <span className="text-white">{policy.diagnostic_center || "None assigned yet"}</span>
              </div>
              <div className="flex justify-between border-b border-slate-800/60 pb-1.5">
                <span className="text-slate-400">Chief Medical Underwriter:</span>
                <span className="text-white font-semibold">{policy.underwriting_doctor || "Dr. Arvind Subramanian"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Initial Premium Payment UTR:</span>
                <span className="text-emerald-400 font-mono font-bold">
                  {policy.payment_reference || "Awaiting payment settlement"}
                </span>
              </div>
            </div>
          </div>

          {/* Section 3: Statutory Tax Deduction Endorsement */}
          <div className="p-4 rounded-xl bg-emerald-950/30 border border-emerald-900/40 space-y-2">
            <div className="text-[11px] font-bold text-emerald-300 uppercase tracking-wider flex items-center gap-1.5">
              <Award className="w-3.5 h-3.5 text-emerald-400" />
              Statutory Tax Benefit Certificate Endorsement
            </div>
            <p className="text-slate-300 text-[11px] leading-relaxed">
              This policy qualifies for income tax deductions under <strong>Section 80C</strong> (Life Insurance) or <strong>Section 80D</strong> (Health Floater) of the Income Tax Act, 1961. The official signed certificate PDF is automatically generated upon policy issuance.
            </p>
          </div>

        </div>

        {/* Modal Footer */}
        <div className="p-4 bg-slate-950 border-t border-slate-800 flex items-center justify-between">
          <div className="text-[10px] text-slate-500 font-mono">
            Created: {policy.created_at}
          </div>
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold transition-colors"
          >
            Close Dossier
          </button>
        </div>

      </div>
    </div>
  );
}
